// competitive-detectors.ts
//
// The finding pool behind the competitive insight rail.
//
// Every finding is DETERMINISTIC arithmetic over measured numbers: the ranking
// feed, the per-model competitive payload, and the attribute scores. No LLM
// call, no randomness, and the order is stable for a given run so the rail does
// not reshuffle between visits.
//
// COPY RULES, enforced here because this module owns every string a card shows:
//   * the headline IS the finding, in plain English, with the competitor named
//   * no instrument vocabulary: "funnel", "mention rate", "top-5", "primary
//     recommendation rate", "noise band", "model split", "divergence" never
//     appear. Attribute names (value, trust, clarity) are human words and stay.
//   * no em dashes; a brand referred back to is "it", never "they"
//   * no causal claim beyond what was computed
//
// PHASE 2: a backend endpoint will precompute findings at read time
// (question-ownership, head-to-head co-mention, citation-source). Those merge
// into THIS pool: they are RailFinding values with origin "backend", their
// types are already declared below, and rankFindings scores them alongside the
// client-side ones. Nothing here needs to change to accept them.
import {
  attributeLabel, formatAttributeScore, NOISE_BAND_POINTS, rankAttributeRows,
  type AttributeBrandRow, type AttributeResult, type RankingRow,
} from "@/app/services/brand/competitive-intelligence-api";
import type { MeasuredModel } from "@/app/components/deep-signal/types";

export type FindingType =
  | "whos-winning"
  | "where-lead-lives"
  | "why-they-win"
  | "your-edge"
  | "up-for-grabs"
  | "their-soft-spot"
  | "climber"
  // Declared now so the pool type is stable when the backend starts supplying
  // these. Nothing emits them yet.
  | "question-ownership"
  | "head-to-head"
  | "citation-source";

export interface RailFinding {
  /** Stable within a run: used for React keys and as the final sort tiebreak. */
  id: string;
  type: FindingType;
  /** The story type, in plain words. */
  eyebrow: string;
  /** One sentence that is the finding. */
  headline: string;
  /** Optional second short sentence, only where the template has one. */
  detail?: string;
  /** The one number, rendered large. */
  stat: string;
  /** Set on attribute-scoped findings: drives click-through and the
   *  one-card-per-attribute rule when dealing a view. */
  attribute?: string;
  /** Raw size of the thing found, normalized to 0..1 WITHIN its own type
   *  before types are ranked against each other. */
  magnitude: number;
  origin: "client" | "backend";
}

export interface DetectorInput {
  competitors: RankingRow[];
  client?: RankingRow;
  focalName: string;
  attrs: AttributeResult[];
  models: MeasuredModel[];
  /** Competitor per-model rates, brand -> model key -> percent. */
  perModelByBrand: Record<string, Record<string, number | null> | undefined>;
  /** The measured brand's per-model rates, model key -> percent. */
  focalPerModel: Record<string, number | null>;
  /** Month label of the previous read, e.g. "June". Null when unknown. */
  priorReadMonth: string | null;
}

// ── Shared helpers ─────────────────────────────────────────────────────────

/** The ranking value, unrounded where the payload carries one. */
const val = (r: AttributeBrandRow): number | null =>
  r.score_precise != null ? r.score_precise : (r.score != null ? r.score : null);

const round1 = (n: number): number => Math.round(n * 10) / 10;
const pts = (n: number): string => `${round1(n)} pts`;

/** A rate expressed the way people say it out loud. */
const oneIn = (rate: number): number => Math.max(1, Math.round(1 / rate));

function ordinal(n: number): string {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
}

const sentenceCase = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

/** The competitor in front overall. Primary recommendation decides it; where
 *  that is unmeasured, being named at all does. */
function leadingCompetitor(competitors: RankingRow[]): RankingRow | null {
  const byPrimary = competitors.filter((c) => c.primary_rec != null && c.primary_rec > 0);
  if (byPrimary.length) {
    return byPrimary.reduce((a, b) => ((b.primary_rec as number) > (a.primary_rec as number) ? b : a));
  }
  const byMention = competitors.filter((c) => c.mention != null);
  if (!byMention.length) return null;
  return byMention.reduce((a, b) => ((b.mention as number) > (a.mention as number) ? b : a));
}

/** Ranked measured rows for an attribute, highest first, by VALUE. */
function measuredByValue(attr: AttributeResult): { row: AttributeBrandRow; v: number }[] {
  return attr.brands
    .map((row) => ({ row, v: val(row) }))
    .filter((x): x is { row: AttributeBrandRow; v: number } => x.v != null)
    .sort((a, b) => b.v - a.v);
}

// ── Detectors ──────────────────────────────────────────────────────────────

/** a. Who is actually winning the answer. */
function whosWinning(i: DetectorInput): RailFinding | null {
  const leader = leadingCompetitor(i.competitors);
  if (!leader?.primary_rec) return null;
  const mine = i.client?.primary_rec;
  // Both halves of the template are claims about measured rates; without the
  // client's own rate there is no honest second sentence, so no card.
  if (mine == null) return null;
  return {
    id: `whos-winning:${leader.brand}`,
    type: "whos-winning",
    eyebrow: "Who's winning",
    headline: `${leader.brand} wins the answer 1 in ${oneIn(leader.primary_rec)} times.`,
    detail: mine === 0 ? "You haven't won one yet." : `You win 1 in ${oneIn(mine)}.`,
    stat: `1 in ${oneIn(leader.primary_rec)}`,
    magnitude: leader.primary_rec,
    origin: "client",
  };
}

/** b. Which model the leader's advantage actually sits on. */
function whereLeadLives(i: DetectorInput): RailFinding | null {
  const leader = leadingCompetitor(i.competitors);
  if (!leader) return null;
  const theirs = i.perModelByBrand[leader.brand];
  if (!theirs) return null;

  const seen = i.models
    .map((m) => ({ key: m.key, label: m.label, v: theirs[m.key] }))
    .filter((x): x is { key: string; label: string; v: number } => x.v != null);
  if (seen.length < 2) return null;

  const sorted = [...seen].sort((a, b) => b.v - a.v);
  const hi = sorted[0];
  const lo = sorted[sorted.length - 1];
  const spread = hi.v - lo.v;
  if (spread <= 0) return null;

  const mineOnLo = i.focalPerModel[lo.key];
  if (mineOnLo == null) return null;
  const gapOnLo = lo.v - mineOnLo;

  return {
    id: `where-lead-lives:${leader.brand}:${hi.key}`,
    type: "where-lead-lives",
    eyebrow: "Where their lead lives",
    headline: `${leader.brand}'s lead lives almost entirely on ${hi.label}.`,
    detail: gapOnLo <= 5
      ? `On ${lo.label} you're nearly even.`
      : `On ${lo.label} the gap is ${round1(gapOnLo)} points.`,
    stat: pts(spread),
    magnitude: spread,
    origin: "client",
  };
}

/** c. The attribute where a rival is furthest ahead of the measured brand. */
function whyTheyWin(i: DetectorInput): RailFinding | null {
  let best: { attr: AttributeResult; leader: AttributeBrandRow; me: AttributeBrandRow; gap: number } | null = null;

  for (const a of i.attrs) {
    const me = a.brands.find((b) => b.brand === i.focalName);
    const mine = me ? val(me) : null;
    if (!me || mine == null) continue;
    const rivals = a.brands.filter((b) => b.brand !== i.focalName && val(b) != null);
    if (!rivals.length) continue;
    const leader = rivals.reduce((x, y) => ((val(y) as number) > (val(x) as number) ? y : x));
    const gap = (val(leader) as number) - mine;
    if (gap <= 0) continue;
    if (!best || gap > best.gap) best = { attr: a, leader, me, gap };
  }

  if (!best) return null;
  const label = attributeLabel(best.attr.attribute).toLowerCase();
  return {
    id: `why-they-win:${best.attr.attribute}`,
    type: "why-they-win",
    eyebrow: "Why they win",
    headline: `AI rates ${best.leader.brand} higher on ${label} than anywhere else: `
      + `${formatAttributeScore(best.leader, i.focalName)} to your ${formatAttributeScore(best.me, i.focalName)}.`,
    stat: pts(best.gap),
    attribute: best.attr.attribute,
    magnitude: best.gap,
    origin: "client",
  };
}

/** d. The attribute the measured brand is rated best on. Top two only. */
function yourEdge(i: DetectorInput): RailFinding | null {
  let best: { attribute: string; rank: number; measured: number; score: number } | null = null;

  for (const a of i.attrs) {
    const ranked = rankAttributeRows(a.brands, i.focalName);
    const mine = ranked.find((r) => r.row.brand === i.focalName);
    if (!mine || mine.rank == null) continue;
    const v = val(mine.row);
    if (v == null) continue;
    const measured = ranked.filter((r) => r.rank != null).length;
    if (measured < 2) continue;
    if (!best || mine.rank < best.rank || (mine.rank === best.rank && v > best.score)) {
      best = { attribute: a.attribute, rank: mine.rank, measured, score: v };
    }
  }

  if (!best || best.rank > 2) return null;
  const label = attributeLabel(best.attribute).toLowerCase();
  const place = best.rank === 1 ? "highest" : "second";
  return {
    id: `your-edge:${best.attribute}`,
    type: "your-edge",
    eyebrow: "Your edge",
    headline: `AI rates you ${place} of ${best.measured} measured on ${label}.`,
    stat: ordinal(best.rank),
    attribute: best.attribute,
    magnitude: best.rank === 1 ? 1 : 0.6,
    origin: "client",
  };
}

/** e. Attributes whose top two are too close to separate: nobody owns them. */
function upForGrabs(i: DetectorInput): RailFinding[] {
  const out: RailFinding[] = [];
  for (const a of i.attrs) {
    const measured = measuredByValue(a);
    if (measured.length < 2) continue;
    const margin = measured[0].v - measured[1].v;
    if (margin > NOISE_BAND_POINTS) continue;
    const label = attributeLabel(a.attribute).toLowerCase();
    out.push({
      id: `up-for-grabs:${a.attribute}`,
      type: "up-for-grabs",
      eyebrow: "Up for grabs",
      headline: `${sentenceCase(label)} has no owner.`,
      detail: `${measured[0].row.brand} and ${measured[1].row.brand} are tied at the top, and it's winnable.`,
      stat: pts(margin),
      attribute: a.attribute,
      // Closer to dead level is more interesting.
      magnitude: Math.max(0, NOISE_BAND_POINTS - margin),
      origin: "client",
    });
  }
  return out;
}

/** f. The overall leader's weakest attribute. Third or worse only. */
function theirSoftSpot(i: DetectorInput): RailFinding | null {
  const leader = leadingCompetitor(i.competitors);
  if (!leader) return null;

  let worst: { attribute: string; rank: number; measured: number } | null = null;
  for (const a of i.attrs) {
    const ranked = rankAttributeRows(a.brands, i.focalName);
    const theirs = ranked.find((r) => r.row.brand === leader.brand);
    if (!theirs || theirs.rank == null) continue;
    const measured = ranked.filter((r) => r.rank != null).length;
    if (measured < 3) continue;
    if (!worst || theirs.rank > worst.rank) {
      worst = { attribute: a.attribute, rank: theirs.rank, measured };
    }
  }

  if (!worst || worst.rank < 3) return null;
  const label = attributeLabel(worst.attribute).toLowerCase();
  return {
    id: `their-soft-spot:${worst.attribute}`,
    type: "their-soft-spot",
    eyebrow: "Their soft spot",
    headline: `${leader.brand} ranks ${ordinal(worst.rank)} of ${worst.measured} on ${label}.`,
    detail: "It's beatable there.",
    stat: ordinal(worst.rank),
    attribute: worst.attribute,
    magnitude: worst.rank / worst.measured,
    origin: "client",
  };
}

/** g. Rivals whose position improved since the previous read. Needs two reads:
 *  the feed leaves deltas null until a same-instrument prior read exists. */
function climbers(i: DetectorInput): RailFinding[] {
  if (!i.priorReadMonth) return [];
  const out: RailFinding[] = [];
  for (const c of i.competitors) {
    const d = c.deltas?.primary_rec;
    if (d == null) continue;
    const points = d * 100;
    if (points < 0.05) continue;
    out.push({
      id: `climber:${c.brand}`,
      type: "climber",
      eyebrow: "Climber",
      headline: `${c.brand} is climbing: up ${round1(points)} points since ${i.priorReadMonth}.`,
      stat: `+${round1(points)}`,
      magnitude: points,
      origin: "client",
    });
  }
  return out;
}

// ── Pool, ranking, dealing ─────────────────────────────────────────────────

/** Weight per story type. Who is winning and who is moving lead, because they
 *  are the two questions a competitive read exists to answer. */
const TYPE_WEIGHT: Record<FindingType, number> = {
  "whos-winning": 1.0,
  "climber": 1.0,
  "question-ownership": 0.9,
  "why-they-win": 0.85,
  "head-to-head": 0.85,
  "where-lead-lives": 0.8,
  "up-for-grabs": 0.75,
  "their-soft-spot": 0.7,
  "citation-source": 0.7,
  "your-edge": 0.65,
};

/** Fixed order, used only to break exact score ties deterministically. */
const TYPE_ORDER: FindingType[] = [
  "whos-winning", "climber", "question-ownership", "why-they-win", "head-to-head",
  "where-lead-lives", "up-for-grabs", "their-soft-spot", "citation-source", "your-edge",
];

export function buildFindingPool(i: DetectorInput): RailFinding[] {
  const found: RailFinding[] = [
    whosWinning(i),
    whereLeadLives(i),
    whyTheyWin(i),
    yourEdge(i),
    theirSoftSpot(i),
    ...upForGrabs(i),
    ...climbers(i),
  ].filter((f): f is RailFinding => f != null);
  return rankFindings(found);
}

/**
 * Interestingness ranking. Each finding's magnitude is normalized against the
 * largest magnitude of its OWN type, so a 40 point spread and a 3 point margin
 * are comparable, then weighted by type. Ties fall back to type order and then
 * id, so a given run always produces the same sequence.
 *
 * Backend findings (phase 2) rank here on the same terms.
 */
export function rankFindings(findings: RailFinding[]): RailFinding[] {
  const maxByType = new Map<FindingType, number>();
  for (const f of findings) {
    const m = Math.abs(f.magnitude);
    maxByType.set(f.type, Math.max(maxByType.get(f.type) ?? 0, m));
  }
  const score = (f: RailFinding): number => {
    const max = maxByType.get(f.type) ?? 0;
    const norm = max > 0 ? Math.abs(f.magnitude) / max : 1;
    return norm * (TYPE_WEIGHT[f.type] ?? 0.5);
  };
  return [...findings].sort((a, b) => {
    const d = score(b) - score(a);
    if (Math.abs(d) > 1e-9) return d;
    const t = TYPE_ORDER.indexOf(a.type) - TYPE_ORDER.indexOf(b.type);
    if (t !== 0) return t;
    return a.id.localeCompare(b.id);
  });
}

/**
 * Deal the ranked pool into views. At most one card per attribute per view, so
 * a single view never shows two findings about the same attribute; a deferred
 * finding simply lands in a later view rather than being dropped.
 */
export function dealViews(ordered: RailFinding[], perView = 5): RailFinding[][] {
  const remaining = [...ordered];
  const views: RailFinding[][] = [];

  while (remaining.length) {
    const view: RailFinding[] = [];
    const usedAttributes = new Set<string>();
    for (let idx = 0; idx < remaining.length && view.length < perView;) {
      const f = remaining[idx];
      if (f.attribute && usedAttributes.has(f.attribute)) { idx++; continue; }
      if (f.attribute) usedAttributes.add(f.attribute);
      view.push(f);
      remaining.splice(idx, 1);
    }
    // Everything left shares an attribute already shown: start the next view
    // with one of them rather than looping forever.
    if (!view.length) view.push(remaining.shift() as RailFinding);
    views.push(view);
  }

  return views;
}
